import { Stack, router, useFocusEffect, type Href } from 'expo-router';
import type { SFSymbol } from 'sf-symbols-typescript';
import * as React from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';

import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { ProfileScopeControl } from '@/components/profiles/profile-scope-control';
import { BRAND } from '@sixam/config/brand';
import { shareTenantProfile } from '@/lib/share';
import { Text } from '@sixam/ui/text';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

const PERSONAL_SCOPE_VALUE = 'personal';
const PROFILE_SCOPE_MAX_WIDTH = 116;

type ProfileToolbarKind = 'member' | 'tenant';

export function ProfileTopBar({
  kind,
  onAddPress,
  onOpenMemberQr,
  onSearchChange,
  searchPlaceholder = 'Search profile',
}: Readonly<{
  kind: ProfileToolbarKind;
  onAddPress?: () => void;
  onOpenMemberQr?: () => void;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
}>) {
  const { signOut } = useSession();
  const { activeTenant } = useTenant();
  const profileEditingEnabled = useReleaseFeatureFlag('profileEditing') === true;
  const integrationSettingsEnabled = useReleaseFeatureFlag('integrationSettings') === true;
  const hasSearch = Boolean(onSearchChange);

  if (Platform.OS === 'web') {
    return null;
  }

  async function signOutFromSettings() {
    await signOut();
    router.replace('/(public)/sign-in');
  }

  function shareProfile() {
    if (kind === 'member') {
      onOpenMemberQr?.();
      return;
    }

    if (activeTenant?.slug) {
      void shareTenantProfile({
        tenantName: activeTenant.name,
        tenantSlug: activeTenant.slug,
      });
    }
  }

  function openAccountSettings() {
    router.push('/app/settings/account');
  }

  function openConnectionsSettings() {
    router.push(profileConnectionsHref(kind, activeTenant?.slug));
  }

  function openProfileEdit() {
    router.push(profileEditHref(kind, activeTenant?.slug));
  }

  function addFromProfile() {
    if (onAddPress) {
      onAddPress();
      return;
    }

    if (kind === 'tenant' && activeTenant?.slug) {
      router.push({
        pathname: '/admin/events/new',
        params: { tenant: activeTenant.slug },
      });
      return;
    }

    if (kind === 'tenant') {
      router.push('/admin/events/new');
    }
  }

  return (
    <>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: BRAND.color.ink },
          headerTintColor: BRAND.color.white,
          headerTitle: '',
          headerTransparent: true,
          scrollEdgeEffects: { top: 'hidden' },
        }}
      />
      {hasSearch ? (
        <Stack.SearchBar
          allowToolbarIntegration
          autoCapitalize="none"
          barTintColor="rgba(255, 255, 255, 0.12)"
          headerIconColor={BRAND.color.white}
          hintTextColor="rgba(255, 255, 255, 0.68)"
          obscureBackground={false}
          onCancelButtonPress={() => onSearchChange?.('')}
          onChangeText={(event) => onSearchChange?.(event.nativeEvent.text)}
          onSearchButtonPress={(event) => onSearchChange?.(event.nativeEvent.text)}
          placement="integratedButton"
          placeholder={searchPlaceholder}
          shouldShowHintSearchIcon
          textColor={BRAND.color.white}
          tintColor={BRAND.color.orange}
        />
      ) : null}
      <Stack.Toolbar placement="left" asChild tintColor={BRAND.color.white}>
        <ProfileScopePicker kind={kind} />
      </Stack.Toolbar>
      <Stack.Toolbar placement="right" tintColor={BRAND.color.white}>
        {kind === 'tenant' || onAddPress ? (
          <Stack.Toolbar.Button
            accessibilityLabel={kind === 'member' ? 'Scan runner QR' : 'Create event'}
            icon={toolbarIcon('plus')}
            onPress={addFromProfile}
            separateBackground
            tintColor={BRAND.color.white}
          >
            {toolbarFallbackLabel('Add')}
          </Stack.Toolbar.Button>
        ) : null}
        <Stack.Toolbar.Button
          accessibilityLabel="Share profile"
          icon={toolbarIcon('square.and.arrow.up')}
          onPress={shareProfile}
          tintColor={BRAND.color.white}
        >
          {toolbarFallbackLabel('Share')}
        </Stack.Toolbar.Button>
        <Stack.Toolbar.Menu
          accessibilityLabel="Open profile settings"
          icon={toolbarIcon('gearshape')}
          tintColor={BRAND.color.white}
          title="Settings"
        >
          {profileEditingEnabled ? (
            <Stack.Toolbar.MenuAction icon={toolbarIcon('pencil')} onPress={openProfileEdit}>
              Edit profile
            </Stack.Toolbar.MenuAction>
          ) : null}
          {integrationSettingsEnabled ? (
            <Stack.Toolbar.MenuAction icon={toolbarIcon('link')} onPress={openConnectionsSettings}>
              Connections
            </Stack.Toolbar.MenuAction>
          ) : null}
          <Stack.Toolbar.MenuAction
            icon={toolbarIcon('person.crop.circle')}
            onPress={openAccountSettings}
          >
            Account
          </Stack.Toolbar.MenuAction>
          <Stack.Toolbar.MenuAction
            icon={toolbarIcon('rectangle.portrait.and.arrow.right')}
            onPress={() => void signOutFromSettings()}
          >
            Sign out
          </Stack.Toolbar.MenuAction>
        </Stack.Toolbar.Menu>
      </Stack.Toolbar>
    </>
  );
}

function profileConnectionsHref(kind: ProfileToolbarKind, tenantSlug?: string): Href {
  if (kind === 'tenant' && tenantSlug) {
    return `/admin/${tenantSlug}/settings/integrations` as Href;
  }

  return '/app/settings/integrations' as Href;
}

function profileEditHref(kind: ProfileToolbarKind, tenantSlug?: string): Href {
  if (kind === 'tenant' && tenantSlug) {
    return `/admin/${tenantSlug}/profile/edit` as Href;
  }

  return '/app/profile/edit' as Href;
}

function ProfileScopePicker({ kind }: Readonly<{ kind: ProfileToolbarKind }>) {
  const { isAdmin } = useSession();
  const { activeTenant, memberships, tenants } = useTenant();
  const [pendingScopeLabel, setPendingScopeLabel] = React.useState<string | null>(null);
  useFocusEffect(
    React.useCallback(() => {
      setPendingScopeLabel(null);
    }, [])
  );
  const adminTenants = React.useMemo(
    () =>
      tenants.filter((tenant) => {
        if (isAdmin) {
          return true;
        }
        const role = memberships.find((membership) => membership.tenant_id === tenant.id)?.role;
        return role === 'admin' || role === 'owner';
      }),
    [isAdmin, memberships, tenants]
  );
  const selectedValue = activeTenant?.slug ?? PERSONAL_SCOPE_VALUE;

  function onValueChange(value: string) {
    if (value === selectedValue || pendingScopeLabel) {
      return;
    }

    const nextScopeLabel =
      value === PERSONAL_SCOPE_VALUE
        ? 'Personal'
        : (adminTenants.find((tenant) => tenant.slug === value)?.name ?? 'club');
    setPendingScopeLabel(nextScopeLabel);

    requestAnimationFrame(() => {
      if (value === PERSONAL_SCOPE_VALUE) {
        router.dismissTo('/app/me');
        return;
      }

      const adminProfileHref = {
        pathname: '/admin/[tenant]',
        params: { tenant: value },
      } as const;
      if (kind === 'tenant') {
        router.replace(adminProfileHref);
        return;
      }

      router.push(adminProfileHref);
    });
  }

  if (pendingScopeLabel) {
    return (
      <View
        accessibilityLabel={`Switching to ${pendingScopeLabel}`}
        accessibilityLiveRegion="polite"
        accessibilityRole="progressbar"
        className="flex-row items-center gap-2 px-2"
      >
        <ActivityIndicator color={BRAND.color.orange} size="small" />
        <Text className="font-sans text-sm font-bold text-white">Switching…</Text>
      </View>
    );
  }

  return (
    <ProfileScopeControl
      maxWidth={PROFILE_SCOPE_MAX_WIDTH}
      onValueChange={onValueChange}
      options={[
        { label: 'Personal', value: PERSONAL_SCOPE_VALUE },
        ...adminTenants.map((tenant) => ({ label: tenant.name, value: tenant.slug })),
      ]}
      selectedValue={selectedValue}
      testID="profile-scope-picker"
    />
  );
}

function toolbarIcon(icon: SFSymbol) {
  return process.env.EXPO_OS === 'ios' ? icon : undefined;
}

function toolbarFallbackLabel(label: string) {
  return process.env.EXPO_OS === 'ios' ? undefined : label;
}
